import { z } from 'zod';
import { integrationConfigValuesSchema, integrationDisplayNameSchema, type ConfigField } from './integration-config.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

/** GPS adapter catalogue and installations (gitaumoses4/chukua#84). */

export const TRACKING_CAPABILITIES = ['webhook', 'polling', 'deviceListing', 'geofencePush'] as const;
export type TrackingCapability = (typeof TRACKING_CAPABILITIES)[number];

export interface TrackingAdapterSummary {
  key: string;
  displayName: string;
  logoSvg: string;
  capabilities: TrackingCapability[];
  fields: ConfigField[];
}

/** Tighter than a vendor would need, so a misconfigured installation can't hammer a vendor's API. #270 owns the scheduler that reads it. */
export const POLLING_INTERVAL_SECONDS = { min: 30, max: 3600, default: 60 } as const;

export const TRACKING_INTEGRATION_STATUSES = ['enabled', 'disabled', 'uninstalled'] as const;
export type TrackingIntegrationStatus = (typeof TRACKING_INTEGRATION_STATUSES)[number];

const pollingIntervalSchema = z.number().int().min(POLLING_INTERVAL_SECONDS.min).max(POLLING_INTERVAL_SECONDS.max).nullable();

export const installTrackingIntegrationSchema = z.object({
  adapterKey: z.string().min(1),
  displayName: integrationDisplayNameSchema,
  config: integrationConfigValuesSchema,
  pollingIntervalSeconds: pollingIntervalSchema.optional(),
});
export type InstallTrackingIntegrationInput = z.infer<typeof installTrackingIntegrationSchema>;

/** A secret left out keeps its stored value; one supplied replaces it. */
export const updateTrackingIntegrationSchema = z
  .object({
    displayName: integrationDisplayNameSchema,
    config: integrationConfigValuesSchema,
    pollingIntervalSeconds: pollingIntervalSchema,
  })
  .partial()
  .strict();
export type UpdateTrackingIntegrationInput = z.infer<typeof updateTrackingIntegrationSchema>;

/** `integrationId` lets the edit form test with its stored secrets for any field left blank. */
export const testTrackingConnectionSchema = z.object({
  adapterKey: z.string().min(1),
  config: integrationConfigValuesSchema,
  integrationId: z.uuid().optional(),
});
export type TestTrackingConnectionInput = z.infer<typeof testTrackingConnectionSchema>;

export type TrackingConnectionResult = { ok: true } | { ok: false; reason: string };

export interface TrackingIntegration {
  id: string;
  adapterKey: string;
  adapterName: string;
  displayName: string;
  status: TrackingIntegrationStatus;
  /** Non-secret values only. */
  config: Record<string, string | number | boolean>;
  /** Names of the secret fields that hold a value: the form shows "Saved", never the value. */
  secretsSet: string[];
  pollingIntervalSeconds: number | null;
  lastTestOk: boolean | null;
  lastTestReason: string | null;
  lastTestedAt: string | null;
  /** Polling health (gitaumoses4/chukua#270); null until the first poll. Failures count only polls that ran out of retries. */
  lastPolledAt: string | null;
  lastPollOk: boolean | null;
  consecutivePollFailures: number;
  lastPollError: string | null;
  createdAt: string;
  uninstalledAt: string | null;
}

/** Uninstalled installations are left out unless `status` asks for them. */
export const trackingIntegrationListQuerySchema = defineListQuery({
  sortFields: ['displayName', 'adapterKey', 'createdAt'],
  defaultSort: { sort: 'displayName', order: 'asc' },
  filters: {
    status: listFilter(z.enum(TRACKING_INTEGRATION_STATUSES)),
    adapterKey: listFilter(z.string().min(1)),
  },
});
export type TrackingIntegrationListQuery = z.infer<typeof trackingIntegrationListQuerySchema>;

export interface TrackingDevice {
  id: string;
  label?: string;
}

/** Search term for the device picker; the vendor has no search, so the API filters what it pages through. */
export const trackingDeviceQuerySchema = z.object({
  q: z
    .string()
    .trim()
    .max(100)
    .optional()
    .transform((q) => q || undefined),
});
export type TrackingDeviceQuery = z.infer<typeof trackingDeviceQuerySchema>;

/** `supported` is false for an adapter without `deviceListing`: the form then takes the device id typed. `truncated` when the fleet outgrew the cap. */
export interface TrackingDevicesResult {
  supported: boolean;
  items: TrackingDevice[];
  truncated: boolean;
}
